import Popover from "components/Popover";
import Text from "components/Text";
import { routeConstants } from "constants/routeConstants";
import { useImageSection } from "pages/Profile/HighLight/ImageSection/useImageSection";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "slice";
import { clearUserState } from "slice/user";
import { logoutUser, useLogoutMutation } from "slice/userAuth";
import { AuthState } from "slice/userAuth/types";
import { getNameInitials } from "utils/getNameInitials";
import { useGetAgencyDataQuery } from "slice/user/index";
import {
    AppAvatar,
    Overlay,
    Popup,
    PopupContent,
    UserInfo,
    UserTitle,
} from "./UserProfilePopup.styled";

export const UserProfilePopup: React.FC = React.memo(() => {
    const { first_name, last_name, is_agency, id } = useAppSelector(
            (state) => state.user
        ),

        { access_token } = useAppSelector((state) => state.userAuth),
        [logout, { data }] = useLogoutMutation(),
        { pathname } = useLocation(),
        navigateTo = useNavigate(),
        dispatch = useAppDispatch(),
        { isOpen: isPopoverVisible, close } = useContext(Popover.Context);
    const { images, handleError } = useImageSection();

    const { data: agencyData, isFetching: isFetchingData } = useGetAgencyDataQuery(
        {
            user_id: id ?? "",
        },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const userProfileUrl = useMemo(() => {
        return is_agency ? routeConstants.agencyInformation : routeConstants.profile.root;
    }, [is_agency]);

    const userEditProfileUrl = useMemo(() => {
        return is_agency
            ? routeConstants.profile.editProfile.agency.root +
            routeConstants.profile.editProfile.agency.companyDetails
            : routeConstants.profile.editProfile.root +
            routeConstants.profile.editProfile.personalDetails;
    }, [is_agency]);

    const profileLinkName = useMemo(() => {
        return pathname === userProfileUrl ? "Edit Profile" : "Profile";
    }, [pathname, userProfileUrl]);

    const userNameInitials = useMemo(() => {
        return getNameInitials(`${first_name} ${last_name}`);
    }, [first_name, last_name]);

    const handleProfileNavigation = useCallback(() => {
        close();
        navigateTo(pathname === userProfileUrl ? userEditProfileUrl : userProfileUrl);
    }, [navigateTo, pathname, close, userEditProfileUrl, userProfileUrl]);

    const handleMyPlans = useCallback(() => {
        close();
        navigateTo(routeConstants.pricing);
    }, [navigateTo, close]);

    const handleMyJobListingsNavigation = useCallback(() => {
        close();
        navigateTo(
            routeConstants.jobs.listings.root + "/" + routeConstants.jobs.listings.active
        );
    }, [close, navigateTo]);

    const logOut = useCallback(() => {
        const userAuth = localStorage.getItem("userAuth");
        logout((userAuth as unknown as AuthState)?.access_token);
    }, [logout]);

    useEffect(() => {
        if (data) {
            dispatch(logoutUser());
            dispatch(clearUserState());
        }
    }, [data, dispatch]);

    const profilePicture = useMemo(() => {
        if (is_agency && agencyData?.company_logo) {
            return agencyData.company_logo;
        }
        return images?.profile?.url;
    }, [is_agency, agencyData, images]);

    return (
        <>
            {isPopoverVisible && <Overlay />}
            <AppAvatar size="S">
                {profilePicture ? (
                    <img src={profilePicture} onError={handleError} alt="user profile" />
                ) : (
                    <>{userNameInitials}</>
                )}
            </AppAvatar>
            <Popup>
                <PopupContent>
                    <UserInfo>
                        <AppAvatar size="S">
                            {profilePicture ? (
                                <img
                                    src={profilePicture}
                                    onError={handleError}
                                    alt="user profile"
                                />
                            ) : (
                                <>{userNameInitials}</>
                            )}
                        </AppAvatar>
                        <UserTitle>
                            <Text variant="heading5">
                                {`${first_name} ${last_name}`}
                            </Text>
                        </UserTitle>
                    </UserInfo>
                    <Text onClick={handleProfileNavigation} variant="body1">
                        {profileLinkName}
                    </Text>
                    {is_agency && (
                        <Text onClick={handleMyJobListingsNavigation} variant="body1">
                            My Casting
                        </Text>
                    )}
                    <Text onClick={handleMyPlans} variant="body1">
                        My Plans
                    </Text>
                    <Text onClick={logOut} variant="body1">
                        Log Out
                    </Text>
                </PopupContent>
            </Popup>
        </>
    );
});
UserProfilePopup.displayName = "UserProfilePopup";
